import React, { useEffect } from 'react'
import { createNativeStackNavigator } from '@react-navigation/native-stack'
import { useCurrentUserStore } from '@store'
import { AuthNavigator } from './AuthNavigator'
import { TabNavigator } from './TabNavigator'
import { AppNavigationProp, RootStackParamList } from './../navigationTypes'
import { ProfileNavigator } from './ProfileNavigator'
import { MenuNavigator } from './MenuNavigator'
import { ContactNavigator } from './ContactNavigator'
import MessagesStackNavigator from './MessagesStackNavigator'
import { DetailsGhostProfile } from '@screens'
import { useExtension } from '@hooks'
import { useFcm } from '../../hooks/useFcm'
import { useNavigation } from '@react-navigation/native'

const RootStack = createNativeStackNavigator<RootStackParamList>()

export const MainNavigator = () => {
  const { userID } = useCurrentUserStore()
  const { isExtension, hash } = useExtension()
  const navigation = useNavigation<AppNavigationProp>()
  const isUserLogged = !!userID

  // Bootstrap FCM once the user is authenticated. No-op on web.
  useFcm()
  
  useEffect(() => {
    // If extension mode is detected after initial mount (web hydration / deep link),
    // route through Auth if not logged, then go back to Extension with the same params.
    if (isExtension) {
      // Preserve URL params like: #/extension/:profileId/:payload
      const payload = (hash || '').startsWith('#/extension/')
        ? (hash || '').replace('#/extension/', '')
        : []
      
      const params = {
        profileId: '',
        payload,
      }

      const state = navigation.getState?.()
      const currentIndex =
        state?.index !== undefined && state?.routes?.[state.index]
          ? state.index
          : 0
      const currentRouteName = state?.routes?.[currentIndex]?.name
      const isOnAuthRoute =
        currentRouteName === 'Auth' ||
        (typeof window !== 'undefined' &&
          (window.location?.hash?.includes('/auth/') ||
            window.location?.pathname?.includes('/auth/')))

      if (!isUserLogged) {
        // Stay on Auth if we're already there (or on any /auth/* path), but keep the extension params.
        if (!isOnAuthRoute) {
          navigation.reset({
            index: 0,
            routes: [
              {
                name: 'Auth',
                params,
              } as never,
            ],
          })
        }
        return
      }

      // Logged in: go to Extension (preserve params for DetailsGhostProfile).
      if (currentRouteName !== 'Extension') {
        navigation.reset({
          index: 0,
          routes: [
            {
              name: 'Extension',
              params,
            } as never,
          ],
        })
      }
      return
    }
    // On web (especially in production builds), navigation state can be briefly undefined
    // during hydration. Guard against it to avoid crashes.
    const state = navigation.getState?.()
    const index =
      state?.index !== undefined && state?.routes?.[state.index]
        ? state.index
        : 0
    const currentRouteName = state?.routes?.[index]?.name
    const isOnAuthRoute =
      currentRouteName === 'Auth' ||
      (typeof window !== 'undefined' &&
        (window.location?.hash?.includes('/auth/') ||
          window.location?.pathname?.includes('/auth/')))

    // ✅ Keep web deep-links working:
    // - do NOT blindly reset to Tab when logged-in
    // - only redirect when necessary (logout, or after login from Auth)
    // - keep current route if it begins with /auth/ (e.g. forgot-password with token)

    // If user logs out, force Auth unless already on an auth route
    if (!isUserLogged && !isOnAuthRoute) {
      navigation.reset({
        index: 0,
        routes: [{ name: 'Auth' } as never],
      })
      return
    }

    // If user logs in while currently on Auth, take them to Tab
    if (isUserLogged && currentRouteName === 'Auth') {
      navigation.reset({
        index: 0,
        routes: [{ name: 'Tab' } as never],
      })
      return
    }

    // NOTE: we intentionally do not reset on isExtension here,
    // because it can break deep-linked extension/profile routes.
  }, [hash, isExtension, isUserLogged, navigation])

  return (
    <RootStack.Navigator
      screenOptions={{ headerShown: false }}
      initialRouteName={isExtension ? (isUserLogged ? 'Extension' : 'Auth') : (isUserLogged ? 'Tab' : 'Auth')}
    >
      <RootStack.Screen name="Auth" component={AuthNavigator} />
      <RootStack.Screen name="Tab" component={TabNavigator} />
      <RootStack.Screen name="Contacts" component={ContactNavigator} />
      <RootStack.Screen name="Messages" component={MessagesStackNavigator} />
      <RootStack.Screen name="Profile" component={ProfileNavigator} />
      <RootStack.Screen name="Extension" component={DetailsGhostProfile} />
      <RootStack.Screen name="Menu" component={MenuNavigator} />
    </RootStack.Navigator>
  )
}
